import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Api from 'api';
import { InvoiceInformationMutation, InvoiceInformationResponse } from 'utils/invoiceInformation';

export function invoiceInformationActor() {
  try {
    const legacy = (window as any).TempStars?.User?.getCurrentUser;
    const user = typeof legacy === 'function' ? legacy() : JSON.parse(localStorage.getItem('userAccount') || 'null');
    return user?.id ? `${user.id}:${user.hygienistId || ''}:${user.dentistId || ''}` : '';
  } catch { return ''; }
}

function bounded<T>(work: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([work, new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('Payment information status was not confirmed.')), 15000);
  })]).finally(() => clearTimeout(timer));
}

/** A failed transport must reconcile before another consent or request can be sent. */
export default function useInvoiceInformation(invoiceId: number, disabled = false) {
  const actor = invoiceInformationActor();
  const visit = useRef({ invoiceId, actor, disabled });
  if (visit.current.invoiceId !== invoiceId || visit.current.actor !== actor || visit.current.disabled !== disabled) {
    visit.current = { invoiceId, actor, disabled };
  }
  const mounted = useRef(true);
  const busy = useRef<typeof visit.current | null>(null);
  const sequence = useRef(0);
  const [state, setState] = useState<{ visit: typeof visit.current; data: InvoiceInformationResponse | null; pending: boolean; error: string }>({
    visit: visit.current, data: null, pending: true, error: '',
  });
  useLayoutEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const current = (key: typeof visit.current) => mounted.current && key === visit.current
    && key.actor === invoiceInformationActor() && !key.disabled;
  const load = async (key = visit.current) => {
    const requestSequence = ++sequence.current;
    const started = performance.now();
    const result = await bounded(Api.getInvoiceInformation(key.invoiceId));
    const elapsed = Math.max(0, performance.now() - started);
    // Count the whole transport duration against the privacy window, conservatively.
    if (Number.isFinite(Date.parse(result.serverNow))) result.serverNow = new Date(Date.parse(result.serverNow) + elapsed).toISOString();
    if (!current(key) || requestSequence !== sequence.current) return null;
    if (Number(result.invoiceId) !== key.invoiceId) throw new Error('The invoice information did not match. Refresh its status.');
    setState({ visit: key, data: result, pending: false, error: '' });
    return result;
  };
  const refresh = async () => {
    const key = visit.current;
    if (!current(key) || busy.current === key) return;
    busy.current = key;
    // Do not retain a sensitive value while the fresh status is unknown.
    setState({ visit: key, data: null, pending: true, error: '' });
    try { await load(key); }
    catch { if (current(key)) setState({ visit: key, data: null, pending: false, error: 'Status is unavailable. Refresh status before trying again.' }); }
    finally { if (busy.current === key) busy.current = null; }
  };
  useEffect(() => { if (invoiceId > 0 && actor && !disabled) void refresh(); }, [invoiceId, actor, disabled]); // Each visit owns its response.
  const mutate = async (action: () => Promise<InvoiceInformationMutation>) => {
    const key = visit.current;
    if (!current(key) || busy.current === key || state.visit !== key || !state.data || state.pending) return null;
    busy.current = key;
    setState(previous => ({ ...previous, pending: true, error: '' }));
    let receipt: InvoiceInformationMutation | null = null;
    let rejection = '';
    try { receipt = await bounded(action()); }
    catch (error: any) { rejection = error?.status >= 400 && error?.status < 500 ? error?.userMessage || 'The request was not accepted.' : ''; }
    if (!current(key)) { if (busy.current === key) busy.current = null; return null; }
    try {
      await load(key);
      if (current(key) && !receipt) setState(previous => ({ ...previous,
        error: rejection || 'The latest status is shown. Check it before taking another action.' }));
    } catch {
      if (current(key)) setState({ visit: key, data: null, pending: false,
        error: 'Your request status has not been confirmed. Refresh status before sending again.' });
    } finally { if (busy.current === key) busy.current = null; }
    return current(key) ? receipt : null;
  };
  const visible = state.visit === visit.current && !disabled && actor ? state : { data: null, pending: !disabled, error: '' };
  return { ...visible, refresh, mutate };
}
